"use client";

import {
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { basketStore } from "~/widgets/stores/basket-store";
import type { BasketItem } from "~/widgets/stores/basket-store";
import { BasketBadge } from "@baluu/ui/components/basket/basket-badge";
import {
  formatBasketRowWindow,
  resolveRowTimezone,
} from "@baluu/ui/lib/checkout";
import { BasketDrawer } from "@baluu/ui/components/basket/basket-drawer";
import { rentalTicketLineLabel } from "@baluu/ui/components/basket/basket-item-card";
import type { BasketDrawerItem } from "@baluu/ui/components/basket/basket-drawer";
import { openBasketCheckout } from "~/widgets/lib/open-basket-checkout";
import { validateBasketItems, apiOrigin } from "~/widgets/lib/basket-validate";
import { trackConversion } from "~/widgets/lib/tracking";
import { DRAWER_ABOVE_OVERLAYS_Z, listenForBasketDrawer } from "~/widgets/lib/ensure-basket-element";
import { overlayHostMounted } from "~/widgets/lib/overlay-hosts";
import { drawerNeedsBodyRoot, mountBodyDrawerRoot } from "~/widgets/lib/body-drawer-root";
import { useBasketItems } from "~/widgets/lib/use-basket";
import { countBasketItems } from "@baluu/ui/lib/checkout/basket-surface";

const DEFAULT_API_URL = "https://api.baluu.io";

export type BasketProps = {
  businessId: string;
  apiUrl?: string;
  primaryColor?: string;
  gtagConversionId?: string;
  gtagConversionLabel?: string;
  /** "none": the page shows its own basket button (Website v2's navbar) and
   *  opens the drawer through `openBasket`, so no floating button. */
  badge?: string;
};

function toDrawerItem(item: BasketItem): BasketDrawerItem {
  let title = item.courseName || item.productName || "Gift Card";
  let subtitle = item.schoolName;

  if (item.type === "giftcard" && item.giftcardAmount) {
    title = `Gift Card`;
    subtitle = item.recipientName
      ? `For ${item.recipientName}`
      : item.schoolName;
  }

  return {
    id: item.id,
    type: item.type,
    title,
    subtitle,
    imageUrl: item.imageUrl,
    price: item.price,
    quantity: item.quantity,
    currency: item.currency,
    sessionDate: item.sessionDate,
    // `sessionTime` has existed on the card and been threaded through the
    // drawer since they were written, and nothing ever assigned it -- so the
    // basket showed a date with no time at all. The window is the listing's,
    // which is why the timezone rides on the item.
    // ONE timezone for both, or neither. The window was formatted with a
    // "Europe/London" fallback while the card's date fell back to the BROWSER's
    // zone, so on an item with no stored timezone the two disagreed — a 00:30
    // London class read as "Wed 17 Jun" beside "00:30" from New York. With no
    // timezone there is no honest window, so none is shown.
    // One rule for both drawers — see `formatBasketRowWindow`, which holds
    // the scope decision and the zone rule so the two cannot drift apart.
    sessionTime: formatBasketRowWindow(item),
    // Resolved HERE, once, and handed to the card — it decides the clock
    // from fields the card never receives, and the date it renders has to
    // share that clock with the `sessionTime` above.
    timezone: resolveRowTimezone(item),
    isOnline: item.isOnline,
    tickets: item.ticketSelections?.map((t) => ({
      name: t.ticketTypeName,
      quantity: t.quantity,
      // Same rule as the widget drawer: a rate-named rental ticket must not
      // render "2 x 1 hour" beside a "(2h)" window. Spell out units.
      label: item.bookingType === "rental"
        ? rentalTicketLineLabel(t.quantity, t.ticketTypeName, item.appointmentData?.fractions)
        : undefined,
      price: t.price,
      lineTotal: t.lineTotal,
    })),
    isAvailable: item.isAvailable,
    availabilityMessage: item.availabilityMessage,
    unavailability: item.unavailability,
    priceChanged: item.priceChanged,
    currentPrice: item.currentPrice,
  };
}

export function Basket({
  businessId,
  apiUrl,
  primaryColor,
  gtagConversionId,
  gtagConversionLabel,
  badge,
}: BasketProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  // Decided by each open: over the overlays while an overlay host is on
  // the page (see DRAWER_ABOVE_OVERLAYS_Z), at the drawer's own level
  // otherwise. Kept through the close so it slides out where it was.
  const [drawerAboveOverlays, setDrawerAboveOverlays] = useState(false);
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null);
  // Where the drawer renders when this element isn't straight on <body> (a
  // page placed it, e.g. in a header): a body-level root, so no stacking
  // context of the page's can keep the drawer under an overlay (see
  // drawerNeedsBodyRoot). It lives exactly as long as the in-element
  // container (the ref below, called as that div mounts and unmounts): there
  // before the drawer opens, and through its closing slide.
  const [bodyDrawerRoot, setBodyDrawerRoot] = useState<HTMLElement | null>(null);
  const bodyDrawerRootRef = useRef<{ dispose: () => void } | null>(null);
  const attachPortalContainer = useCallback((container: HTMLDivElement | null) => {
    bodyDrawerRootRef.current?.dispose();
    bodyDrawerRootRef.current = null;
    setPortalContainer(container);
    const root = container?.getRootNode();
    const host = root instanceof ShadowRoot ? root.host : null;
    if (container && drawerNeedsBodyRoot(host)) {
      const created = mountBodyDrawerRoot(host as HTMLElement);
      bodyDrawerRootRef.current = created;
      setBodyDrawerRoot(created.mount);
    } else {
      setBodyDrawerRoot(null);
    }
  }, []);
  const [hostedReturnSuccess, setHostedReturnSuccess] = useState(false);
  const hostedReturnHandled = useRef(false);
  /**
   * In-flight count rather than a boolean, so two overlapping validates cannot
   * have the faster one report "done" while the other is still asking.
   */
  const [validatesInFlight, setValidatesInFlight] = useState(0);
  const isValidating = validatesInFlight > 0;
  /** Which validate is the current one — see `runValidate`. */
  const validateSeqRef = useRef(0);

  /**
   * The drawer's `isValidating` is handed to BasketDrawer, which disables
   * Checkout while it is true — so on THIS surface the timeout is also how
   * long a bad connection can hold that button shut, and it fires on every
   * open. The overlay keeps the patient default; here a stale verdict is worth
   * less than a responsive button, and the overlay revalidates anyway.
   */
  const DRAWER_VALIDATE_TIMEOUT_MS = 6_000;

  /**
   * Ask the server about this basket and stamp the answer onto it.
   *
   * Reads the items from the store rather than closing over the `items`
   * snapshot below, deliberately: applying a response mints new item
   * identities, so a callback that depended on `items` would change on every
   * response. That is precisely the shape that turned the overlay's debounced
   * validate into a ~2Hz poll. With only `businessId` and `apiUrl` as deps
   * this callback is stable, and nothing re-fires it but a drawer opening.
   *
   * Sends the buyer's email when any surface on this page has learned one —
   * see `_knownBuyerEmail` in the store. It is what lets the server exclude
   * their own reservation holds; without it a buyer who reached Pay and came
   * back is measured against himself and hard-blocked.
   */
  const runValidate = useCallback(async () => {
    const current = basketStore.getItems(businessId);
    if (current.length === 0) return;

    // Last call wins. Opening, closing and reopening quickly can leave two in
    // flight, and the older response would otherwise overwrite the newer one.
    const seq = ++validateSeqRef.current;
    setValidatesInFlight((n) => n + 1);
    try {
      const results = await validateBasketItems({
        apiUrl: apiUrl || DEFAULT_API_URL,
        schoolId: businessId,
        items: current,
        // Whatever the checkout overlay has already learned this page load.
        // Without it the server cannot exclude this buyer's own reservation
        // holds, and a basket they hold seats for comes back sold out to
        // them — a hard blocker, on the one button that leads to the email
        // field which would clear it. Undefined on a fresh page load, which
        // is the honest anonymous case.
        buyerEmail: basketStore.getKnownBuyerEmail(),
        // This surface has no email field, so an identity refusal here is a
        // dead end: it disables the Checkout button that is the only route to
        // the field which would change it. The overlay adjudicates those.
        canActOnIdentityVerdicts: false,
        timeoutMs: DRAWER_VALIDATE_TIMEOUT_MS,
      });
      if (seq !== validateSeqRef.current) return;
      if (results) basketStore.applyValidationResults(results);
    } catch {
      // Non-blocking. A basket the server can't be reached about keeps
      // whatever verdict it has; checkout re-validates server-side anyway.
    } finally {
      // Unconditional: a superseded call has still finished, and the count is
      // what keeps concurrent ones honest.
      setValidatesInFlight((n) => n - 1);
    }
  }, [businessId, apiUrl]);

  /**
   * The one way the drawer opens, so the validate cannot be forgotten on a
   * path — the badge, the `baluu:open-basket-drawer` event, and the Sheet's
   * own open change all come through here.
   *
   * Validating on OPEN rather than on page load is the deliberate choice.
   * This bundle runs on other people's marketing pages: revalidating every
   * pageview that happens to have a basket would bill those sites for a
   * request before anything needing a verdict is on screen. And an
   * `items`-dependent effect — the other obvious way to do it — re-fires on
   * its own result, which is the bug fixed in the overlay one commit ago.
   * An event fires once.
   */
  const openDrawer = useCallback(() => {
    setDrawerAboveOverlays(overlayHostMounted());
    setIsDrawerOpen(true);
    void runValidate();
  }, [runValidate]);

  const handleDrawerOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        openDrawer();
        return;
      }
      // Retire whatever is still in flight. Closing the drawer usually means
      // heading INTO the checkout overlay, whose email-bearing validate is the
      // better-informed answer — and this one is anonymous, so landing late it
      // would clear the member price the overlay had just resolved. Worse, the
      // overlay's dedup key is claimed by then, so nothing would re-ask.
      // Bumping the sequence makes the pending response discard itself.
      validateSeqRef.current += 1;
      setIsDrawerOpen(false);
    },
    [openDrawer],
  );

  // Requests to open the drawer (after "Add to basket", a page's own basket
  // button), including one made before this element had mounted.
  useEffect(() => listenForBasketDrawer(businessId, openDrawer), [businessId, openDrawer]);

  // Detect return from hosted Stripe checkout
  useEffect(() => {
    if (hostedReturnHandled.current) return;
    if (typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    const checkoutStatus = params.get("baluu_checkout");
    const basketOrderId = params.get("basket_order_id");

    if (checkoutStatus === "success" && basketOrderId) {
      hostedReturnHandled.current = true;

      const checkoutSessionId = params.get("checkout_session_id");

      // Clean query params from URL without reload
      const url = new URL(window.location.href);
      url.searchParams.delete("baluu_checkout");
      url.searchParams.delete("basket_order_id");
      url.searchParams.delete("redirect_status");
      url.searchParams.delete("payment_intent");
      url.searchParams.delete("checkout_session_id");
      window.history.replaceState({}, "", url.toString());

      // Process the payment on the backend
      const baseUrl = apiOrigin(apiUrl || DEFAULT_API_URL);
      fetch(`${baseUrl}/widget/app/processBasketPayment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ basketOrderId, checkoutSessionId }),
      })
        .then((res) => {
          if (res.ok) basketStore.clearBasket(businessId);
        })
        .catch(() => {
          // Webhooks will handle processing; clear basket optimistically
          basketStore.clearBasket(businessId);
        });

      setHostedReturnSuccess(true);

      // The buyer was redirected to Stripe's hosted page and back, so the
      // in-memory basket totals are gone. The checkout overlay stashes the
      // order value before redirecting (see BasketCheckoutOverlay) — read it
      // back so the conversion carries value/currency/email for Google Ads.
      let stashed: {
        grossTotal?: number;
        netTotal?: number;
        total?: number;
        discountAmount?: number;
        currency?: string;
        email?: string;
      } = {};
      try {
        const raw = window.sessionStorage.getItem(
          `baluu_basket_conv_${basketOrderId}`,
        );
        if (raw) {
          stashed = JSON.parse(raw);
          window.sessionStorage.removeItem(`baluu_basket_conv_${basketOrderId}`);
        }
      } catch {
        // sessionStorage may be unavailable (private mode / cross-origin)
      }

      // Fire the documented purchase event (`baluu_booking_completed`) so the
      // GTM trigger schools set up per our conversion-tracking guide matches,
      // and fire the zero-code Google Ads conversion when the gtag attributes
      // are present on the component. Also mirror the basket-specific event
      // name (dataLayer only) for forward compatibility.
      trackConversion(
        "baluu_booking_completed",
        {
          businessId,
          purchaseId: basketOrderId,
          ...stashed,
        },
        gtagConversionId && gtagConversionLabel
          ? { conversionId: gtagConversionId, conversionLabel: gtagConversionLabel }
          : undefined,
        ["baluu_basket_checkout_completed"],
      );

      window.dispatchEvent(
        new CustomEvent("baluu:basket-checkout-completed", {
          detail: { basketOrderId, hostedReturn: true },
        }),
      );

      // Also dispatch the documented `baluu:booking-completed` DOM event so the
      // "Custom JavaScript" conversion approach (which listens for it) captures
      // basket purchases too.
      document.dispatchEvent(
        new CustomEvent("baluu:booking-completed", {
          bubbles: true,
          composed: true,
          detail: { businessId, purchaseId: basketOrderId, ...stashed },
        }),
      );

      // Auto-dismiss after 8 seconds
      setTimeout(() => setHostedReturnSuccess(false), 8000);
    } else if (checkoutStatus === "cancelled") {
      hostedReturnHandled.current = true;
      const url = new URL(window.location.href);
      url.searchParams.delete("baluu_checkout");
      window.history.replaceState({}, "", url.toString());
    }
  }, [businessId, apiUrl, gtagConversionId, gtagConversionLabel]);

  const items = useBasketItems(businessId);

  const itemCount = countBasketItems(items);

  // An emptied basket closes the drawer (see the render below): retire the
  // anonymous validate it may have in flight, as handleDrawerOpenChange does,
  // so it can't land over the next checkout's email-bearing one.
  useEffect(() => {
    if (itemCount === 0) validateSeqRef.current += 1;
  }, [itemCount]);

  const currency = items[0]?.currency || "GBP";

  const drawerItems = items.map(toDrawerItem);

  const handleRemoveItem = useCallback(
    (id: string) => {
      basketStore.removeItem(id);
      // Revalidate, exactly as handleReduceQuantity does. Until the trial
      // allowance landed, no verdict's remedy was removing a DIFFERENT item,
      // so a sibling holding a now-stale verdict was unreachable.
      // TRIAL_LIMIT_REACHED is the first — and the overlay copy tells the
      // buyer to do precisely this — so without a revalidate they remove the
      // offending trial and the other line keeps a verdict that no longer
      // applies, leaving Checkout disabled until the drawer is reopened.
      //
      // Safe in general: a removal only ever loosens a verdict, never adds
      // demand. `runValidate` already no-ops on an empty basket, so removing
      // the last item costs nothing.
      void runValidate();
    },
    [runValidate],
  );

  const handleClearBasket = useCallback(() => {
    basketStore.clearBasket(businessId);
  }, [businessId]);

  const handleUpdateItemPrice = useCallback(
    (id: string, newPrice: number) => {
      basketStore.updateItem(id, {
        price: newPrice,
        priceChanged: false,
        currentPrice: undefined,
      });
    },
    [],
  );

  /**
   * Accept the "Reduce to N" auto-fix on a partial-capacity / partial-stock
   * item, then re-ask so the verdict clears (or doesn't) on server truth
   * rather than on our arithmetic.
   *
   * Only ever reached for an item WITHOUT ticket selections: the card withholds
   * this action from a ticketed line, where the server derives the quantity it
   * measures from `ticketSelections` and writing `quantity` alone would leave
   * the verdict identical while desyncing the displayed total.
   */
  const handleReduceQuantity = useCallback(
    (id: string, newQuantity: number) => {
      if (newQuantity <= 0) {
        basketStore.removeItem(id);
        return;
      }
      // Drop the verdict along with the quantity. It said "you asked for 5,
      // only 2 left"; we are now asking for 2, so it no longer describes this
      // item. Left in place, a validate that fails or is refused would keep
      // rendering "Reduce to 2" on a line already reduced to 2, with nothing
      // to show the reduction was taken.
      basketStore.updateItem(id, {
        quantity: newQuantity,
        unavailability: undefined,
        availabilityMessage: undefined,
      });
      void runValidate();
    },
    [runValidate],
  );

  /**
   * Retry after a VALIDATION_TRANSIENT — the verdict that means the CHECK
   * failed, not that the item is gone. Until this surface validated at all,
   * the button existed and did nothing.
   */
  const handleRetryValidation = useCallback(() => {
    void runValidate();
  }, [runValidate]);

  const handleCheckout = useCallback(() => {
    // Same reason as the open-change handler: the overlay is about to ask a
    // better-informed question, so this surface's pending answer must not
    // land on top of it.
    validateSeqRef.current += 1;
    setIsDrawerOpen(false);
    openBasketCheckout({
      businessId,
      apiUrl,
      primaryColor,
      gtagConversionId,
      gtagConversionLabel,
    });
  }, [businessId, apiUrl, primaryColor, gtagConversionId, gtagConversionLabel]);

  const handleContinueShopping = useCallback(() => {
    // Retire a pending validate here too, for the same reason as the other two
    // close paths: its answer is about a drawer nobody is looking at, and
    // applying it late only mints new item identities and wakes subscribers.
    validateSeqRef.current += 1;
    setIsDrawerOpen(false);
  }, []);

  // Show success toast for hosted checkout return even with empty basket
  if (hostedReturnSuccess) {
    return (
      <div
        style={{
          position: "fixed",
          bottom: "24px",
          right: "24px",
          zIndex: 9999,
          background: "#fff",
          borderRadius: "12px",
          boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
          padding: "16px 20px",
          maxWidth: "360px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          fontFamily: "system-ui, sans-serif",
        }}
      >
        <div
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            background: "#dcfce7",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6 9 17l-5-5" />
          </svg>
        </div>
        <div>
          <p style={{ fontWeight: 600, fontSize: "14px", margin: 0, color: "#111" }}>
            Order Complete!
          </p>
          <p style={{ fontSize: "13px", color: "#666", margin: "2px 0 0" }}>
            You&apos;ll receive a confirmation email shortly.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setHostedReturnSuccess(false)}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "4px",
            color: "#999",
            flexShrink: 0,
          }}
          aria-label="Dismiss"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>
    );
  }

  // Nothing renders on an empty basket, the drawer included, so removing the
  // last item closes it rather than leaving it "open" unseen — to spring back
  // with the next item, over the basket checkout "Book now" just opened. Its
  // in-flight validate is retired as on every other close (the effect above).
  if (itemCount === 0 && isDrawerOpen) setIsDrawerOpen(false);
  if (itemCount === 0) return null;

  return (
    <>
      {badge === "none" ? null : (
        <BasketBadge
          itemCount={itemCount}
          onClick={openDrawer}
          variant="floating"
        />
      )}

      <div ref={attachPortalContainer} />

      <BasketDrawer
        zIndex={drawerAboveOverlays ? DRAWER_ABOVE_OVERLAYS_Z : undefined}
        isOpen={isDrawerOpen}
        onOpenChange={handleDrawerOpenChange}
        items={drawerItems}
        currency={currency}
        isValidating={isValidating}
        onRemoveItem={handleRemoveItem}
        onClearBasket={handleClearBasket}
        onUpdateItemPrice={handleUpdateItemPrice}
        onReduceQuantity={handleReduceQuantity}
        onRetryValidation={handleRetryValidation}
        onCheckout={handleCheckout}
        onContinueShopping={handleContinueShopping}
        portalContainer={bodyDrawerRoot ?? portalContainer}
      />
    </>
  );
}
